import flat from 'flat';
import { nodePathToFriendlyName } from '@ourbranch/lookups';

import { CAR_DIFF_PATHS_TO_OMIT } from 'core/helpers/diff-paths';
import { groupValidationErrors } from 'core/components/validation-errors-modal/validation-errors-modal';
import { formatSegment } from 'core/components/validation-errors-modal/enhance-field-name';

// Same noise the policy-changes list skips (parse-requested-changes.js).
const IGNORED_DIFF_SEGMENTS = ['id', 'removedTimestamp', ...CAR_DIFF_PATHS_TO_OMIT];

// "cars.0.limitRental" → "Cars 1 Limit Rental"
const pathLabel = (path) =>
  nodePathToFriendlyName[path] ??
  path
    .split('.')
    .map((segment) => (/^\d+$/.test(segment) ? String(Number(segment) + 1) : formatSegment(segment)))
    .join(' ');

// Validation errors to list in the drawer as "field label: message": the ones outside the chosen change's sections,
// which the agent can't see, or all of them for "Something else", where a long form hides them below the fold.
export const getDrawerErrors = (errors, values, changeType) => {
  const flatErrors = flat(errors ?? {});
  const listed = Object.fromEntries(
    Object.entries(flatErrors).filter(([path]) => !changeType?.sections || !changeType.covers(path))
  );
  // Farm location paths keep the modal's "Location 1, (Barn)" labels; others read "People 1 First Name".
  return groupValidationErrors(listed, values)
    .flatMap(({ errors: sectionErrors }) => sectionErrors)
    .map((error) =>
      error.fieldName.includes('farmLocations') ? error : { ...error, fieldLabel: pathLabel(error.fieldName) }
    );
};

// Fields a preview changes beyond the chosen change, named for a warning. The server diffs the whole segment, so a
// stray edit made before switching change types shows up here. Empty for "Something else" or an unreadable diff.
export const getChangesOutsideType = (diff, changeType) => {
  if (!diff || !changeType?.sections) {
    return [];
  }
  let diffObj;
  try {
    diffObj = JSON.parse(diff);
  } catch {
    return [];
  }
  const paths = [
    ...Object.keys(diffObj?.filtered?.addedChanges ?? {}),
    ...Object.keys(diffObj?.filtered?.removedChanges ?? {})
  ];
  const labels = paths
    .filter((path) => !path.split('.').some((segment) => IGNORED_DIFF_SEGMENTS.includes(segment)))
    .filter((path) => !changeType.covers(path))
    .map(pathLabel);
  return [...new Set(labels)];
};
